import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  METRIC_KEYS, METRIC_LABEL, effectiveBriefing, formatCount, parseCount, parseEffectiveBriefing, type BriefingRow, type EffectiveBriefing, type MetricKey,
} from "@talentkontor/domain";
import { api } from "../../data";
import { Dialog } from "../../components/ui";
import { BriefingAnsicht } from "../oeffentlich/MarkeBausteine";
import { ladeScreenshotHoch, trageKennzahlenEin, type MetricsFile, type MetricsRow } from "../agentur/kooperationen/marke-daten";
import type { CreatorScope } from "./data";
import type { CDeliverable } from "./data";
import { dateOf, useT } from "./sprache";
import { useCreatorAction, useCreatorContext } from "./ui";

/**
 * Creator-Portal zur Marke (0073): Briefing der Kooperation und je Beitrag lesen, Stand „bei der Marke“, Kennzahlen mit
 * Screenshots hochladen. Der Creator sieht nie Kontakt, Adresse oder den internen Kommentar der Marke; nur, was die Agentur
 * weitergibt (Feedback an der Einreichung).
 */

export interface CreatorMarke {
  /** Offene und entschiedene Freigaben (Sicht talent_brand_reviews: nur Stand und Zeitpunkte) */
  reviews: { id: string; deliverable_id: string | null; submission_id: string; status: string; created_at: string; decided_at: string | null }[];
  briefings: BriefingRow[];
  /** Wirksames Briefing der Kooperation (mit Rückfall auf das Briefing der Kampagne) */
  dealBriefing: EffectiveBriefing;
  metrics: MetricsRow[];
  metricsFiles: MetricsFile[];
  /** Ab wann Kennzahlen erwartet werden, je Beitrag (public.deliverable_metrics_due: Einstellung der Agentur, Stories eigener Wert) */
  metricsDue: Record<string, string>;
}

export async function loadCreatorMarke(s: CreatorScope, dealId: string, deliverableIds: readonly string[]): Promise<CreatorMarke> {
  const quiet = <T,>(p: Promise<T[]>) => p.catch(() => [] as T[]);
  const [reviews, briefings, dealBriefing, metrics, metricsFiles, metricsDue] = await Promise.all([
    quiet(api.select<CreatorMarke["reviews"][number]>("talent_brand_reviews", {
      columns: "id, deliverable_id, submission_id, status, created_at, decided_at", filters: [["org_id", "eq", s.orgId], ["deal_id", "eq", dealId]], order: [["created_at", "desc"]],
    })),
    quiet(api.select<BriefingRow>("deal_briefings", {
      columns: "id, deal_id, deliverable_id, key_messages, dos, donts, hashtags, mentions, ad_label, paid_partnership, deadline, notes, updated_at",
      filters: [["org_id", "eq", s.orgId], ["deal_id", "eq", dealId]],
    })),
    api.read<unknown>("briefing_view", { p_deal: dealId }).then(parseEffectiveBriefing).catch(() => parseEffectiveBriefing(null)),
    quiet(api.select<MetricsRow>("deliverable_metrics", { filters: [["org_id", "eq", s.orgId], ["deal_id", "eq", dealId]] })),
    deliverableIds.length
      ? quiet(api.select<MetricsFile>("attachments", {
          columns: "id, owner_id, storage_path, file_name, mime_type, size_bytes, created_at",
          filters: [["owner_kind", "eq", "metrics"], ["owner_id", "in", [...deliverableIds]], ["deleted_at", "is", null]], order: [["created_at", "asc"]],
        }))
      : Promise.resolve([] as MetricsFile[]),
    api.read<Record<string, string> | null>("deliverable_metrics_due", { p_deal: dealId }).then((r) => r ?? {}).catch(() => ({} as Record<string, string>)),
  ]);
  return { reviews, briefings, dealBriefing, metrics, metricsFiles, metricsDue };
}

export function useCreatorMarke(dealId: string, deliverableIds: readonly string[]) {
  const { scope } = useCreatorContext();
  const ids = [...deliverableIds].sort();
  return useQuery({ queryKey: ["creator", scope.talentId, "marke", dealId, ids.join(",")], queryFn: () => loadCreatorMarke(scope, dealId, ids) });
}

/** Briefing eines Beitrags: seine eigenen Angaben ergänzen das Briefing der Kooperation (wie app.briefing_effective). */
export function beitragsBriefing(m: CreatorMarke, deliverableId: string): EffectiveBriefing | null {
  const own = m.briefings.find((b) => b.deliverable_id === deliverableId);
  if (!own) return null;
  const deal = m.briefings.find((b) => !b.deliverable_id) ?? null;
  return effectiveBriefing(deal, own, null);
}

/** Offener Link bei der Marke für diesen Beitrag? */
export function beiDerMarke(m: CreatorMarke | undefined, deliverableId: string): boolean {
  return !!m?.reviews.some((r) => r.deliverable_id === deliverableId && r.status === "open");
}

export function DealBriefing({ briefing }: { briefing: EffectiveBriefing }) {
  const { lang } = useCreatorContext();
  const t = useT();
  if (briefing.source === "none") return null;
  return (
    <section className="panel stack" aria-labelledby="briefing-titel">
      <h3 id="briefing-titel">{t("Briefing", "Briefing")}</h3>
      <BriefingAnsicht briefing={briefing} lang={lang} />
      <p className="source-note">{t("Hashtags und Markierungen gehören in die Caption; die Kennzeichnung als Werbung an den Anfang.", "Hashtags and tags go into the caption; the ad label at the beginning.")}</p>
    </section>
  );
}

/** Kurzfassung der eingetragenen Kennzahlen. */
export function kennzahlenText(m: MetricsRow | undefined, lang: "de" | "en"): string | null {
  if (!m) return null;
  const teile = METRIC_KEYS.filter((k) => m[k] != null).slice(0, 3).map((k) => `${METRIC_LABEL[k][lang]} ${formatCount(m[k]!, lang)}`);
  return teile.join(" · ") || null;
}

/** Ab wann die Kennzahlen erwartet werden: rechnet die Datenbank aus den Einstellungen der Agentur (0 Tage = keine Anfrage). */
export function kennzahlenAb(m: CreatorMarke | undefined, deliverableId: string): string | null {
  return m?.metricsDue[deliverableId] ?? null;
}

export function KennzahlenCreatorDialog({ beitrag, name, metrics, dateien, onClose }: {
  beitrag: CDeliverable; name: string; metrics: MetricsRow | undefined; dateien: MetricsFile[]; onClose: () => void;
}) {
  const { scope, lang, today } = useCreatorContext();
  const t = useT();
  const { busy, error, run } = useCreatorAction();
  const [werte, setWerte] = useState<Record<MetricKey, string>>(() => Object.fromEntries(METRIC_KEYS.map((k) => [k, metrics?.[k] != null ? String(metrics[k]) : ""])) as Record<MetricKey, string>);
  const [stand, setStand] = useState(metrics?.captured_on ?? today);
  const [datei, setDatei] = useState<File | null>(null);
  const zahlen = useMemo(() => Object.fromEntries(METRIC_KEYS.map((k) => [k, parseCount(werte[k])])) as Record<MetricKey, number | null>, [werte]);
  const falsch = METRIC_KEYS.some((k) => Number.isNaN(zahlen[k]));
  const leer = METRIC_KEYS.every((k) => zahlen[k] == null) && dateien.length === 0;
  return (
    <Dialog open onClose={onClose} title={t(`Kennzahlen · ${name}`, `Metrics · ${name}`)}>
      <form className="stack" onSubmit={(e) => {
        e.preventDefault();
        if (falsch || leer) return;
        void run("werte", () => trageKennzahlenEin(api, beitrag.id, zahlen, stand, null).then(() => true as const),
          t("Danke! Deine Agentur nimmt die Werte in den Bericht auf.", "Thanks! Your agency will add the numbers to the report.")).then((r) => r && onClose());
      }}>
        <p>{t("Lade Screenshots deiner Statistik hoch (Instagram, TikTok, YouTube) und trag die Werte ein. Leere Felder bleiben leer, nicht 0.",
          "Upload screenshots of your insights (Instagram, TikTok, YouTube) and enter the numbers. Empty fields stay empty, not 0.")}</p>
        <div className="stack">
          <strong>{t(`Screenshots (${dateien.length})`, `Screenshots (${dateien.length})`)}</strong>
          {dateien.map((d) => <span key={d.id}>{d.file_name} · {dateOf(d.created_at.slice(0, 10), lang)}</span>)}
          <div className="row">
            <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" aria-label={t("Screenshot wählen", "Choose screenshot")} onChange={(e) => setDatei(e.target.files?.[0] ?? null)} />
            <button type="button" disabled={!datei || busy !== null} onClick={() => datei && void run("datei", () => ladeScreenshotHoch(api, scope.orgId, beitrag.id, datei), t("Screenshot hochgeladen.", "Screenshot uploaded.")).then(() => setDatei(null))}>
              {t("Hochladen", "Upload")}
            </button>
          </div>
        </div>
        <div className="grid">
          {METRIC_KEYS.map((k) => (
            <label key={k}>{METRIC_LABEL[k][lang]}<small>{lang === "en" ? METRIC_LABEL[k].hint_en : METRIC_LABEL[k].hint_de}</small>
              <input inputMode="numeric" value={werte[k]} onChange={(e) => setWerte((w) => ({ ...w, [k]: e.target.value }))} aria-invalid={Number.isNaN(zahlen[k])} />
            </label>
          ))}
        </div>
        {falsch && <p className="tk-error" role="alert">{t("Bitte Zahlen eintragen (z. B. 12300, 12.300 oder 1,2k).", "Please enter numbers (e.g. 12300, 12,300 or 1.2k).")}</p>}
        <label>{t("Stand der Werte", "Numbers as of")}<input type="date" value={stand} min={beitrag.published_on ?? undefined} max={today} onChange={(e) => setStand(e.target.value)} /></label>
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row">
          <button className="primary" type="submit" disabled={busy !== null || falsch || leer}>{t("Kennzahlen senden", "Send metrics")}</button>
          <button type="button" onClick={onClose}>{t("Schließen", "Close")}</button>
        </div>
      </form>
    </Dialog>
  );
}
